import "./navbar.css";
import React, { useState, useEffect, useMemo } from "react";
import { Slider, Tooltip } from "antd"; // Import Tooltip
import { debounce } from "lodash";

function Navbar({ filters, onScoreChanged }) {
  const [localFilters, setLocalFilters] = useState(filters || {});
  const [sectionData, setSectionData] = useState(null);
  const [uiData, setUIData] = useState(null);

  const currentPath = window.location.pathname.replace(/\/$/, "") || "/index";
  const navLinks = [
    { label: "比数量", href: "/compare.html", match: "/compare" },
    { label: "按专业", href: "/special.html", match: "/special" },
    { label: "按学校", href: "/index.html", match: ["/index", "/"] },
  ].filter((link) => {
    const matches = Array.isArray(link.match) ? link.match : [link.match];
    return !matches.some(
      (m) => currentPath === m || currentPath === m + ".html",
    );
  });

  const debouncedOnScoreChanged = useMemo(
    () =>
      debounce((uiData) => {
        let score = uiData?.score;
        setLocalFilters((prev) => ({ ...prev, score }));
        if (onScoreChanged) {
          onScoreChanged(uiData);
          // console.log('Debounced score changed',score);
        }
      }, 1000),
    [onScoreChanged],
  );

  const Silder_onChangeComplete = (score) => {
    console.log("score changed", score);
    if (sectionData) {
      let { newSection, newSectionStr } = getSection(score, sectionData);
      setUIData((prev) => {
        let newUIData = {
          ...prev,
          score: score,
          section: newSection,
          sectionStr: newSectionStr,
        };
        debouncedOnScoreChanged(newUIData);
        return newUIData;
      });
    }
  };
  const handleYearChange = (field, value) => {
    setUIData((prev) => {
      let newUIData = {
        ...prev,
        selectYear: Number(value),
      };
      debouncedOnScoreChanged(newUIData);
      return newUIData;
    });
  };
  // Initial load: fetch all score range data
  useEffect(() => {
    fetch("/api/section") // Used to trigger backend logic and get all data
      .then((res) => res.json())
      .then((data) => {
        if (data?.data?.length > 0) {
          setSectionData(data.data);
          const initScore = localFilters.score ?? data.data[0].score;
          let { newSection, newSectionStr } = getSection(initScore, data.data);
          let currYear = new Date().getFullYear();

          let newUIData = {
            year: data.year,
            score: initScore,
            section: newSection,
            sectionStr: newSectionStr,
            minScore: Math.min(...data.data.map((item) => item.score)),
            maxScore: Math.max(...data.data.map((item) => item.score)),
            years: [],
          };
          // 如果月份大于等于9月 years: [currYear,currYear - 1, currYear - 2]
          if (new Date().getMonth() >= 7) {
            newUIData.years = [currYear, currYear - 1, currYear - 2];
            newUIData.selectYear = currYear;
          } else {
            newUIData.years = [currYear - 1, currYear - 2, currYear - 3];
            newUIData.selectYear = currYear - 1;
          }
          setUIData(newUIData);
          onScoreChanged(newUIData);
        }
      })
      .catch(() => {
        setSectionData(null);
        setUIData(null);
      });
  }, []);

  const Silder_onChange = (newValue) => {
    setUIData((prev) => ({ ...prev, score: newValue }));
  };

  // Function to generate the tooltip content
  const generateTooltipContent = () => {
    if (!uiData) return "加载中...";

    const { year, score, sectionStr, selectYear } = uiData;

    // // Example dynamic values for demonstration. Replace with actual logic.
    // const rankChange150 = sectionStr ? parseInt(sectionStr.split('-')[0] || sectionStr) + 150 : '--';
    // const rankChangeMinus150 = sectionStr ? parseInt(sectionStr.split('-')[0] || sectionStr) - 150 : '--';
    // const rankChange500 = sectionStr ? parseInt(sectionStr.split('-')[0] || sectionStr) + 500 : '--';
    // const rankChangeMinus500 = sectionStr ? parseInt(sectionStr.split('-')[0] || sectionStr) - 500 : '--';

    return (
      <div>
        <div>
          <span style={{ color: "#FFD700" }}>
            {year}年{score}分
          </span>{" "}
          排名
          <span style={{ color: "#FFD700" }}>{sectionStr}</span>
        </div>
        <div>
          查询的是{selectYear}年排名在
          <span style={{ color: "#FFD700" }}> -500</span> 到
          <span style={{ color: "#FFD700" }}> +500</span> 之间
        </div>
        <div>的专业组，排名颜色：</div>
        <div>
          <span style={{ color: "yellow" }}>黄: +-150</span>,
          <span style={{ color: "red" }}> 红: -500到-150</span>,
          <span style={{ color: "green" }}> 绿: 150到500</span>
        </div>
      </div>
    );
  };

  return (
    <nav className="navbar">
      <div className="logo">
        <img src="/logo.png" alt="Logo" />
        京志愿
      </div>

      <div className="navbar-filters-right">
        {uiData &&
          uiData.years.map((year) => (
            <label key={year}>
              <input
                type="radio"
                name="year"
                value={year}
                checked={uiData.selectYear === year}
                onChange={(e) => handleYearChange("year", e.target.value)}
              />
              {year}
            </label>
          ))}
      </div>
      <div className="navbar-filters-right">
        <Tooltip title={generateTooltipContent()} placement="bottom">
          {" "}
          {/* Add Tooltip here */}
          <div className="readonly-info">
            {uiData ? (
              <div>
                {uiData?.year || "--"}年 排名:{" "}
                <span className="rank-number">
                  {uiData?.sectionStr?.toLocaleString() || "--"}
                </span>
              </div>
            ) : (
              <div>排名：--</div>
            )}
          </div>
        </Tooltip>
        <label> | 分数:</label>
        <span className="score-display">{localFilters.score || "--"}</span>
        <div className="slider-container">
          {uiData ? (
            <Slider
              min={uiData?.minScore}
              max={uiData?.maxScore}
              value={uiData?.score}
              onChange={Silder_onChange}
              onChangeComplete={Silder_onChangeComplete} // This is where the debounced call will be triggered
              tooltip={{
                formatter: (val) =>
                  `${uiData?.year || "--"}年 ${val}分 排名${uiData?.sectionStr || "--"}`,
              }}
              reverse
            />
          ) : (
            <span>加载中...</span> // You can display a loading state
          )}
        </div>
        <div className="nav-links">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} className="nav-link">
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );

  function getSection(score, sectionData) {
    const matched = sectionData.find((item) => item.score === score);
    let newSection, newSectionStr;
    if (matched) {
      newSection = matched.section;
      newSectionStr = newSection.toString();
    } else {
      // 找到分数所在的区间边界（降序假设score越大，section越小）
      // 先按 score 升序排序，防止数据乱序
      const sortedData = [...sectionData].sort((a, b) => a.score - b.score);

      let lower = null;
      let upper = null;

      for (let i = 0; i < sortedData.length - 1; i++) {
        const curr = sortedData[i];
        const next = sortedData[i + 1];
        if (score > curr.score && score < next.score) {
          lower = curr;
          upper = next;
          break;
        }
      }

      // 如果score比最小score还小，取最小区间
      if (!lower && !upper && score < sortedData[0].score) {
        lower = sortedData[0];
        upper = sortedData[1];
      }

      // 如果score比最大score还大，取最大区间
      if (!lower && !upper && score > sortedData[sortedData.length - 1].score) {
        lower = sortedData[sortedData.length - 2];
        upper = sortedData[sortedData.length - 1];
      }

      if (lower && upper) {
        // section是排名，假设section越小排名越靠前，区间排名是section最大到最小区间
        const minSection = Math.min(lower.section, upper.section);
        const maxSection = Math.max(lower.section, upper.section);
        newSection = maxSection;
        newSectionStr = `${minSection}-${maxSection}`;
      } else {
        newSection = -1;
        newSectionStr = "--";
      }
    }
    return { newSection, newSectionStr };
  }
}

export default Navbar;
